<script setup>
import {computed, inject, onMounted, ref, watch} from 'vue';
import {useRoute, useRouter} from "vue-router";
import {useCommunityPublicInformationStore} from "@/stores/communityPublicInformationStore";
import {useDsoInformationStore} from "@/stores/dsoCompanyInformationStore";
import {useI18n} from "vue-i18n";
import { useSnackbarStore } from "@es-bill/es-bill-common-components";

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const $log = inject('log');
const formValidation = inject('$formValidation');
const communityStore = useCommunityPublicInformationStore();
const dsoStore = useDsoInformationStore();
const snackbarStore = useSnackbarStore();

const existingCommunity = ref(undefined);
const valid = ref(false);
const name = ref('');
const ecn = ref('');
const ecid = ref('');
const communityType = ref('');
const communityTypes = ['16a', '16b', '16c', 'EP', 'SC', 'P2P'];
const dso = ref('');
const ecnDso = ref('');
const selectedDso = ref('');
const legal = ref('');
const identificationType = ref('');
const identificationTypes = [
  'ZVR',
  'FBN',
  'FB',
  'DateOfBirth',
  'CommunityNumber',
  'Firmenbuchnummer',
  'Vereinsregisterauszug',
  'Wohnungseigentümergenossenschaft',
  'WEG',
  'other'
];
const identification = ref('');
const logoSrc = ref('');
const uriMoreInfo = ref('');
const communityNotes = ref('');
const allow0MeteringPoints = ref(false);
const isDiscoverable = ref(false);
const needsSepaUpfront = ref(false);
const transformerReference = ref('');
const substationReference = ref('');
const addressPostal = ref({ name: '', address: '', addressExtra: '', zip: '', city: '' });
const contacts = ref([{ name: '', emailAddress: '', phone: '' }]);
const customAgreements = ref([{ id: '', description: '', link: '' }]);
const sepaAgreement = ref({ id: '', description: '', link: '' });
const formRef = ref(null);

const isNewCommunity = computed(() => !existingCommunity.value);

// ADD: computed property to control whether ECID field is editable
const canEditEcid = computed(() => {
  // ECID can be edited for new communities or for existing communities that don't have an ECID yet
  return isNewCommunity.value || !existingCommunity.value?.ecid;
});

// ADD: computed property for DSO autocomplete items
const dsoInformationsAvailable = computed(() => {
  return dsoStore.informations.map(dsoInfo => ({
    title: dsoInfo.name,
    value: dsoInfo.name,
    ecn: dsoInfo.ecn,
    ...dsoInfo
  }));
});

const dsoFilter = (value, query, item) => {
  if (!query) return true;
  const search = query.toString().toLowerCase();
  const titleMatch = (value ?? '').toString().toLowerCase().includes(search);
  const ecnMatch = (item?.raw?.ecn ?? '').toString().toLowerCase().includes(search);
  const nameMatch = (item?.name ?? item?.title ?? '').toString().toLowerCase().includes(search);
  return titleMatch || ecnMatch || nameMatch;
};

watch(() => route.params.id, loadExistingCommunity);
onMounted(() => {
  loadExistingCommunity(route.params.id);
  // Fetch DSO information when component mounts
  dsoStore.fetchDsoInformation();
});

function onDsoSelected(selectedEcn) {
  if (selectedEcn) {
    const selectedDsoInfo = dsoStore.informations.find(dsoInfo => dsoInfo.ecn === selectedEcn);
    if (selectedDsoInfo) {
      dso.value = selectedDsoInfo.name;
      ecnDso.value = selectedDsoInfo.ecn;
    }
  } else {
    // Clear both fields when selection is cleared
    dso.value = '';
    ecnDso.value = '';
  }
}

function loadExistingCommunity(ecnIdParam) {
  if (ecnIdParam) {
    communityStore.getCommunityByEcn(ecnIdParam).then(community => {
      if (!community) {
        $log.error('No community data received');
        return;
      }

      $log.debug('community', community);
      existingCommunity.value = community;

      // Check that each property exists before trying to set .value on it
      if (community.name) name.value = community.name;
      if (community.ecn) ecn.value = community.ecn;
      if (community.ecid) ecid.value = community.ecid;
      if (community.communityType) communityType.value = community.communityType;
      dso.value = community.dso || '';
      ecnDso.value = community.ecnDso || '';
      // Set selectedDso based on the ecnDso value
      selectedDso.value = community.ecnDso || '';
      legal.value = community.legal || '';
      identificationType.value = community.identificationType || '';
      identification.value = community.identification || '';
      logoSrc.value = community.logoSrc || '';
      uriMoreInfo.value = community.uriMoreInfo || '';
      communityNotes.value = community.communityNotes || '';
      allow0MeteringPoints.value = Boolean(community.allow0MeteringPoints);
      isDiscoverable.value = Boolean(community.isDiscoverable);
      needsSepaUpfront.value = Boolean(community.needsSepaUpfront);
      transformerReference.value = community.transformerReference || '';
      substationReference.value = community.substationReference || '';
      addressPostal.value = community.addressPostal || { name: '', address: '', addressExtra: '', zip: '', city: '' };
      contacts.value = Array.isArray(community.contacts) && community.contacts.length ? community.contacts : [{ name: '', emailAddress: '', phone: '' }];
      customAgreements.value = Array.isArray(community.customAgreements) && community.customAgreements.length ? community.customAgreements : [{ id: '', description: '', link: '' }];
      sepaAgreement.value = community.sepaAgreement || { id: '', description: '', link: '' };
    }).catch(error => {
      $log.error('Error loading community:', error);
    });
  }
}

// Shared URL validator
const validateUrl = value => {
  if (!value) return true;
  const pattern = /^https?:\/\/(?:[\w-]{2,}\.)+[\w-]{2,}(\/[\w-.,@?^=%&:/~+#]*[\w-@?^=%&/~+#])?$/;
  return pattern.test(value) || t('enterValidUrl');
};

// ECN is required elsewhere; this validator only checks format when value present
const validateEcn = value => { // TODO move to common components
  if (!value) return true; // required rule handled separately
  const pattern = /^(?:RC|GC|CC|RD|GD|CD|EP|SC|PP)\d{6}$/;
  return pattern.test(value) || t('enterValidEcn');
};

// ECID is optional; only validate format when provided
const validateEcid = value => { // TODO move to common components
  if (!value) return true;
  const pattern = /^AT[C0-9]{2}\d{4}[A-Z0-9]{5}(?:RC|GC|CC|RD|GD|CD|SC|PP)\d{6}[A-Z0-9]{12}$/;
  return pattern.test(value) || t('enterValidEcid');
};

const validateEcnDso = value => {
  if (!value) return true; // Not required for all types
  const pattern = /^AT\d{6}$/;
  return pattern.test(value) || t('enterValidEcnDso');
};

// 'Required' validation is provided by the shared FormValidationService

function checkCommunityTypeMatchesEcn(ecn, communityType) {
  if (/^(GC|GD)\d{6}$/.test(ecn)) return communityType === '16a';
  if (/^(CC|CD)\d{6}$/.test(ecn)) return communityType === '16b';
  if (/^(RC|RD)\d{6}$/.test(ecn)) return communityType === '16c';
  if (/^EP\d{6}$/.test(ecn)) return communityType === 'EP';
  if (/^SC\d{6}$/.test(ecn)) return communityType === 'SC';
  if (/^PP\d{6}$/.test(ecn)) return communityType === 'P2P';
  return true;
}

// the DSO selection is required for SC and P2P (October 2026 update)
const dsoRequired = computed(() => communityType.value === 'SC' || communityType.value === 'P2P');

const validateDsoSelection = value => {
  if (!dsoRequired.value) return true;
  return !!value || t('dsoRequiredForType');
};

const submitForm = async () => {
  // Trigger form validation
  const { valid: isValid } = await formRef.value.validate();
  if (!isValid) {
    snackbarStore.showSnackbar(t('validationErrors'), '', 'Error', 6000, []);
    return;
  }

  // After validation, check that community type matches ECN
  if (!checkCommunityTypeMatchesEcn(ecn.value, communityType.value)) {
    snackbarStore.showSnackbar(t('BadRequestCommunityTypeEcn'), '', 'Error', 10000, []);
    return;
  }
  // Filter out empty contacts and custom agreements
  const filteredContacts = contacts.value.filter(contact =>
    contact.name || contact.emailAddress || contact.phone
  );

  const filteredCustomAgreements = customAgreements.value.filter(agreement =>
    agreement.description || agreement.link
  );

  // Prepare community data payload
  const communityData = {
    name: name.value,
    communityType: communityType.value,
    legal: legal.value,
    dso: dso.value,
    ecnDso: ecnDso.value,
    identificationType: identificationType.value,
    identification: identification.value,
    logoSrc: logoSrc.value,
    uriMoreInfo: uriMoreInfo.value,
    communityNotes: communityNotes.value,
    allow0MeteringPoints: allow0MeteringPoints.value,
    isDiscoverable: isDiscoverable.value,
    needsSepaUpfront: needsSepaUpfront.value,
    transformerReference: transformerReference.value,
    substationReference: substationReference.value,
    addressPostal: addressPostal.value,
    contacts: filteredContacts,
    customAgreements: filteredCustomAgreements,
    ...(needsSepaUpfront.value && (sepaAgreement.value.description || sepaAgreement.value.link)
      ? { sepaAgreement: sepaAgreement.value }
      : {}),
  };
  if (existingCommunity.value) {
    // If ECID was previously not set and user provided one, include it in the update payload
    if (!existingCommunity.value.ecid && ecid.value && ecid.value.trim()) {
      communityData.ecid = ecid.value.trim();
    }
    communityStore.updateCommunity(existingCommunity.value.ecn, communityData)
      .then(() => {
        snackbarStore.showSnackbar(t('applicantMessages.updateSuccess'), '', 'Success', 4000, []);
        router.push({ name: 'communities' });
      })
      .catch(error => {
        const errorCode = error?.response?.data?.code;
        let errorMessage;
        if (errorCode === 'ECID_DUPLICATE') {
          errorMessage = t('errorEcidDuplicate');
        } else {
          errorMessage = error?.response?.data?.message || error?.message || t('unexpectedError');
        }
        const validationDetails = error?.response?.data?.details ?
          ': ' + Object.entries(error.response.data.details).map(([field, msg]) => `${field}: ${msg}`).join(', ') : '';
        snackbarStore.showSnackbar(errorMessage + validationDetails, '', 'Error', 10000, []);
        $log.error('Error updating community:', error);
      });
  } else {
    communityData.ecn = ecn.value;
    // Only include ecid if it has a value - omit entirely if empty to work with sparse index
    if (ecid.value && ecid.value.trim()) {
      communityData.ecid = ecid.value.trim();
    }
    // Note: ecid is omitted (undefined) when empty, not set to null, due to sparse unique index
    communityStore.createCommunity(communityData)
      .then(() => {
        snackbarStore.showSnackbar(t('requestSuccess'), '', 'Success', 4000, []);
        router.push({ name: 'communities' });
      })
      .catch(error => {
        const errorCode = error?.response?.data?.code;
        let errorMessage;
        if (errorCode === 'ECID_DUPLICATE') {
          errorMessage = t('errorEcidDuplicate');
        } else {
          errorMessage = error?.response?.data?.message || error?.message || t('unexpectedError');
        }
        const validationDetails = error?.response?.data?.details ?
          ': ' + Object.entries(error.response.data.details).map(([field, msg]) => `${field}: ${msg}`).join(', ') : '';
        snackbarStore.showSnackbar(errorMessage + validationDetails, '', 'Error', 10000, []);
        $log.error('Error creating community:', error);
      });
  }
};

// AddressPostal validation rules
const addressPostalRules = {
  name: [formValidation.formValidationRequired],
  address: [formValidation.formValidationRequired],
  zip: [formValidation.formValidationZipCode, formValidation.formValidationRequired],
  city: [formValidation.formValidationRequired]
};

// Contacts validation rules - conditional validation
const getContactNameRules = (contact) => {
  // Name is required if any field in the contact is filled
  const hasAnyData = contact.name || contact.emailAddress || contact.phone;
  return hasAnyData ? [formValidation.formValidationRequired] : [];
};

const getContactEmailRules = (contact) => {
  // Email validation only if email is provided
  return contact.emailAddress ? [formValidation.formValidationEmail] : [];
};

const getContactPhoneRules = (contact) => {
  // Phone validation only if phone is provided
  return contact.phone ? [formValidation.formValidationPhone] : [];
};

// CustomAgreements validation rules - conditional validation
const getCustomAgreementDescriptionRules = (agreement) => {
  // Description is required if link is provided (but not if both are empty)
  return agreement.link && !agreement.description ? [formValidation.formValidationRequired] : [];
};

const getCustomAgreementLinkRules = (agreement) => {
  // Link validation only if link is provided
  return agreement.link ? [validateUrl] : [];
};
</script>

<template>
  <v-container>
    <v-form ref="formRef" v-model="valid">
      <h1 class="mb-10">{{ $t('publicCommunityInformationForm') }}</h1>

      <v-text-field
        bg-color="white"
        :label="$t('name')"
        v-model="name"
        variant="outlined"
        :rules="[formValidation.formValidationRequired]"
        required
        clearable
      ></v-text-field>

      <v-text-field
        bg-color="white"
        :label="$t('ecn')"
        v-model="ecn"
        variant="outlined"
        :rules="[formValidation.formValidationRequired, validateEcn]"
        clearable
        :disabled="!isNewCommunity"
      ></v-text-field>

      <v-text-field
        bg-color="white"
        :label="$t('ecid')"
        v-model="ecid"
        variant="outlined"
        :rules="[validateEcid]"
        clearable
        :disabled="!canEditEcid"
      ></v-text-field>

      <v-select
        bg-color="white"
        :label="$t('communityType')"
        v-model="communityType"
        :items="communityTypes"
        variant="outlined"
        :rules="[formValidation.formValidationRequired]"
        required
        clearable
        :disabled="!isNewCommunity"
      ></v-select>

      <v-autocomplete
        bg-color="white"
        :label="$t('dso')"
        v-model="selectedDso"
        :items="dsoInformationsAvailable"
        :rules="[validateDsoSelection]"
        variant="outlined"
        item-title="title"
        item-value="ecn"
        clearable
        :filter-keys="['title', 'ecn']"
        @update:model-value="onDsoSelected"
        :custom-filter="dsoFilter"
      >
        <template v-slot:item="{ props, item }">
          <v-list-item v-bind="props">
            <!-- Primary title is provided by props.title (DSO name). We only add ECN as subtitle -->
            <v-list-item-subtitle>{{ item.raw.ecn }}</v-list-item-subtitle>
          </v-list-item>
        </template>
      </v-autocomplete>

      <v-text-field
        bg-color="white"
        :label="$t('legalForm')"
        v-model="legal"
        variant="outlined"
        :rules="[formValidation.formValidationRequired]"
        required
        clearable
      ></v-text-field>

      <v-select
        bg-color="white"
        :label="$t('identificationType')"
        v-model="identificationType"
        :items="identificationTypes"
        variant="outlined"
        :rules="[formValidation.formValidationRequired]"
        required
        clearable
      ></v-select>

      <v-text-field
        bg-color="white"
        :label="$t('identification')"
        v-model="identification"
        variant="outlined"
        :rules="[formValidation.formValidationRequired]"
        required
        clearable
      ></v-text-field>

      <v-text-field
        bg-color="white"
        :label="$t('logoSrc')"
        v-model="logoSrc"
        variant="outlined"
        :rules="[validateUrl]"
        clearable
      ></v-text-field>

      <v-text-field
        bg-color="white"
        :label="$t('uriMoreInfo')"
        v-model="uriMoreInfo"
        variant="outlined"
        :rules="[validateUrl]"
        clearable
      ></v-text-field>

      <v-checkbox
        v-model="allow0MeteringPoints"
        :label="$t('allow0MeteringPoints')"
      ></v-checkbox>

      <v-checkbox
          v-model="isDiscoverable"
          :label="$t('isDiscoverable')"
      ></v-checkbox>

      <v-checkbox
          v-model="needsSepaUpfront"
          :label="$t('needsSepaUpfront')"
      ></v-checkbox>
      <div class="ml-8" v-if="needsSepaUpfront">
        <h3>{{ $t('sepaAgreement') }}</h3>
        <v-text-field
          bg-color="white"
          :label="$t('description')"
          v-model="sepaAgreement.description"
          variant="outlined"
          :rules="getCustomAgreementDescriptionRules(sepaAgreement)"
          clearable
        ></v-text-field>
        <v-text-field
          bg-color="white"
          :label="$t('link')"
          v-model="sepaAgreement.link"
          variant="outlined"
          :rules="getCustomAgreementLinkRules(sepaAgreement)"
          clearable
        ></v-text-field>
      </div>
      <v-divider v-if="needsSepaUpfront" class="my-4"></v-divider>

      <v-textarea
        bg-color="white"
        :label="$t('communityNotes')"
        v-model="communityNotes"
        variant="outlined"
        clearable
        auto-grow
        rows="3"
      ></v-textarea>

      <v-text-field
        bg-color="white"
        :label="$t('transformerReference')"
        v-model="transformerReference"
        variant="outlined"
        clearable
      ></v-text-field>

      <v-text-field
        bg-color="white"
        :label="$t('substationReference')"
        v-model="substationReference"
        variant="outlined"
        clearable
      ></v-text-field>

      <v-divider class="my-4"></v-divider>
      <h3>{{ $t('addressPostal') }}</h3>
      <v-text-field
        bg-color="white"
        :label="$t('name')"
        v-model="addressPostal.name"
        variant="outlined"
        clearable
        :rules="addressPostalRules.name"
      ></v-text-field>
      <v-text-field
        bg-color="white"
        :label="$t('address')"
        v-model="addressPostal.address"
        variant="outlined"
        clearable
        :rules="addressPostalRules.address"
      ></v-text-field>
      <v-text-field
        bg-color="white"
        :label="$t('addressExtra')"
        v-model="addressPostal.addressExtra"
        variant="outlined"
        clearable
      ></v-text-field>
      <v-text-field
        bg-color="white"
        :label="$t('zipCode')"
        v-model="addressPostal.zip"
        variant="outlined"
        clearable
        :rules="addressPostalRules.zip"
      ></v-text-field>
      <v-text-field
        bg-color="white"
        :label="$t('city')"
        v-model="addressPostal.city"
        variant="outlined"
        clearable
        :rules="addressPostalRules.city"
      ></v-text-field>

      <v-divider class="my-4"></v-divider>
      <h3>{{ $t('contacts') }}</h3>
      <div v-for="(contact, idx) in contacts" :key="'contact-' + idx" class="mb-2">
        <v-text-field
          bg-color="white"
          :label="$t('name')"
          v-model="contact.name"
          variant="outlined"
          clearable
          :rules="getContactNameRules(contact)"
        ></v-text-field>
        <v-text-field
          bg-color="white"
          :label="$t('emailAddress')"
          v-model="contact.emailAddress"
          variant="outlined"
          clearable
          :rules="getContactEmailRules(contact)"
        ></v-text-field>
        <v-text-field
          bg-color="white"
          :label="$t('phoneNumber')"
          v-model="contact.phone"
          variant="outlined"
          clearable
          :rules="getContactPhoneRules(contact)"
        ></v-text-field>
        <TertiaryButton v-if="contacts.length > 1" prepend-icon="mdi-delete" :text="''" @click="contacts.splice(idx, 1)" />
      </div>
      <SecondaryButton class="mb-6" size="small" @click="contacts.push({ name: '', emailAddress: '', phone: '' })" :text="$t('add')" />

      <v-divider class="my-4"></v-divider>
      <h3>{{ $t('customAgreements') }}</h3>
      <div v-for="(agreement, idx) in customAgreements" :key="'agreement-' + idx" class="mb-2">
        <v-text-field
          bg-color="white"
          :label="$t('description')"
          v-model="agreement.description"
          variant="outlined"
          clearable
          :rules="getCustomAgreementDescriptionRules(agreement)"
        ></v-text-field>
        <v-text-field
          bg-color="white"
          :label="$t('link')"
          v-model="agreement.link"
          variant="outlined"
          clearable
          :rules="getCustomAgreementLinkRules(agreement)"
        ></v-text-field>
        <TertiaryButton v-if="customAgreements.length > 1" prepend-icon="mdi-delete" :text="''" @click="customAgreements.splice(idx, 1)" />
      </div>
      <SecondaryButton class="mb-10" size="small" @click="customAgreements.push({ description: '', link: '' })" :text="$t('add')" />

      <v-row justify="end" class="mt-8">
        <v-col cols="auto">
          <PrimaryButton @click="submitForm" :text="$t('submit')" />
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<style scoped>
</style>
